import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/system';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import DialogActions from '@mui/material/DialogActions';

import { getHeaderAndNavigationTypography } from 'styles/helpers/getThemeStyles';
import { calculateRgbaWithAlpha } from 'helpers/colorFormatter';

import { OPACITY_70 } from 'constants/opacity';
import {
  ITEM_DESCRIPTION_AND_PRICES,
  MENU_SECTION_TITLES
} from 'styles/constants/appearanceSectionNames';
import { GROUP_ORDER_CHECKOUT_CONFIRMATION } from 'bento-ordering/group_ordering/constants/groupOrder';
import {
  GROUP_ORDERING_DEADLINE_INPUT_FORMAT,
  GROUP_ORDERING_DEADLINE_OUTPUT_FORMAT
} from 'bento-ordering/base/constants/timeFormat';
import { useRedirectToOOMenu } from 'bento-ordering/group_ordering/hooks';

import {
  selectCartGroupOrderDeadline,
  selectCartGroupOrderSlug,
  selectCheckoutUrl,
  selectGroupOrderErrorIs404
} from 'bento-ordering/cart/selectors/index';

import PrimaryCta from 'components/Buttons/PrimaryCta';
import SecondaryCta from 'components/Buttons/SecondaryCta';

import GroupOrderErrorToast from 'bento-ordering/group_ordering/components/GroupOrderErrorToast';
import { formatDateTime } from 'bento-ordering/base/utils/timeHelpers';
import { updateGroupOrder } from 'bento-ordering/group_ordering/actions';
import { heapClearProperties } from 'user_analytics/heapMain';

const StyledHeaderText = styled(Typography)(({ theme }) => ({
  fontSize: 20,
  lineHeight: 1.25,
  color: theme.palette.sectionNav.link.main,
  marginBottom: '14px'
}));

const StyleDescriptionText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.mediumSoy,
  fontFamily: theme.typography.fontFamily,
  fontWeight: '450'
}));

const GroupOrderCheckoutConfirmation = ({ toggleIsOpen }) => {
  const groupOrderDeadline = useSelector(selectCartGroupOrderDeadline);
  const dispatch = useDispatch();
  const groupOrderSlug = useSelector(selectCartGroupOrderSlug);
  const checkoutUrl = useSelector(selectCheckoutUrl);
  const errorIs404NotFound = useSelector(selectGroupOrderErrorIs404);

  const redirectToOO = useRedirectToOOMenu();

  const groupOrderCheckoutButtonStyles = theme => ({
    color: calculateRgbaWithAlpha(
      theme.palette?.button?.background?.hover,
      OPACITY_70
    ),
    ...getHeaderAndNavigationTypography(theme)
  });

  const onConfirmHandler = async event => {
    event.preventDefault();

    const result = await dispatch(
      updateGroupOrder(groupOrderSlug, { cart_locked: true })
    );

    if (result?.error !== true) {
      window.location.replace(checkoutUrl);

      // Clearing cookie after redirect to capture that event as well
      heapClearProperties();
    }
  };

  return (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
      justifyContent="center"
    >
      <>
        <StyledHeaderText component="h1" variant={MENU_SECTION_TITLES}>
          {GROUP_ORDER_CHECKOUT_CONFIRMATION.title}
        </StyledHeaderText>
        {groupOrderDeadline && (
          <StyleDescriptionText
            variant={ITEM_DESCRIPTION_AND_PRICES}
            marginBottom={2}
          >
            {`${GROUP_ORDER_CHECKOUT_CONFIRMATION.deadline} ${formatDateTime(
              groupOrderDeadline,
              GROUP_ORDERING_DEADLINE_INPUT_FORMAT,
              GROUP_ORDERING_DEADLINE_OUTPUT_FORMAT
            )}`}
          </StyleDescriptionText>
        )}
        <StyleDescriptionText
          variant={ITEM_DESCRIPTION_AND_PRICES}
          marginBottom={1}
        >
          {GROUP_ORDER_CHECKOUT_CONFIRMATION.description}
        </StyleDescriptionText>
        <GroupOrderErrorToast />
        <DialogActions>
          <Box mt={2}>
            <PrimaryCta
              label={
                errorIs404NotFound
                  ? GROUP_ORDER_CHECKOUT_CONFIRMATION.startover
                  : GROUP_ORDER_CHECKOUT_CONFIRMATION.confirm
              }
              handleClick={errorIs404NotFound ? redirectToOO : onConfirmHandler}
              fullWidth
            />
          </Box>
          <Box mt={2}>
            <SecondaryCta
              label={GROUP_ORDER_CHECKOUT_CONFIRMATION.cancel}
              disabled={errorIs404NotFound}
              handleClick={toggleIsOpen}
              fullWidth
              sx={groupOrderCheckoutButtonStyles}
            />
          </Box>
        </DialogActions>
      </>
    </Box>
  );
};

GroupOrderCheckoutConfirmation.propTypes = {
  toggleIsOpen: PropTypes.func.isRequired
};

export default GroupOrderCheckoutConfirmation;
